import Config from '../../../config';
let config;
import features from 'lib/common/features';
import TessituraAPI from './tessitura-wrapper';
import fallbacks from 'lib/common/fallbacks';
import logger from 'lib/common/logger';
import { enhanceSeatData, setScreenZoneData } from 'lib/common/transforms';

import get from 'lodash/get';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import indexOf from 'lodash/indexOf';

import PerformanceMap from 'lib/tessitura/JSONAPI/maps/Performance';
import PackageMap from 'lib/tessitura/JSONAPI/maps/Package';
import ScreenMap from 'lib/tessitura/JSONAPI/maps/Screen';
import SectionMap from 'lib/tessitura/JSONAPI/maps/Section';
import SeatMap from 'lib/tessitura/JSONAPI/maps/Seat';
import SeatStatusMap from 'lib/tessitura/JSONAPI/maps/SeatStatus';
import CartMap from 'lib/tessitura/JSONAPI/maps/Cart';

let API;
let INITIAL_SESSION_KEY;
let jsonpath = 'assets/json-responses';

export function configure(endpoint, initialSessionKey) {
  let apiInstance = new TessituraAPI(endpoint, initialSessionKey);
  API = apiInstance;

  if (initialSessionKey) {
    setInitialSessionKey(initialSessionKey);
  }
}

export function getInitialSessionKey() {
  return INITIAL_SESSION_KEY || '%sessionKey%';
}

export function setInitialSessionKey(sessionKey) {
  INITIAL_SESSION_KEY = sessionKey;
  return INITIAL_SESSION_KEY;
}

export function getSessionKey() {
  let initialSessionKey = getInitialSessionKey();
  if (initialSessionKey) {
    return Promise.resolve(initialSessionKey);
  } else {
    return API.getSessionKey();
  }
}

export function loginInfo() {
  return API.requestGet({
    method: `${jsonpath}/LoginInfo.json`
  }).then(response => {
    if (has(response, 'result.ModeOfSaleId')) {
      return {
        MOS: response.result.ModeOfSaleId,
        promotion_code: response.result.SourceId
      };
    } else {
      logger.captureMessage('Failed to load LoginInfoEx');
      return false;
    }
  });
}

export function StoreADAData(data) {
  return API.requestPut({
    method: `Web/Cart/%sessionKey%/Properties`,
    params: {
      Notes: data
    }
  });
}

export function getPromoCode(promoCode, sourceCode) {
  return API.request({
    method: `Web/Session/%sessionKey%/PromoCode`,
    params: {
      PromoCodeString: promoCode,
      PromoCode: sourceCode
    }
  });
}

export function removePromoCode() {
  // Update the source number and mode of sale, then refresh
  return API.requestPut({
    method: 'Web/Cart/%sessionKey%/Properties',
    params: {
      SourceId: features.getOption('promoCode', 'defaultSourceNo'),
      ModeOfSaleId: features.getOption('promoCode', 'defaultModeOfSaleId')
    }
  }).then(() => {
    window.location.reload();
    return true;
  });
}

export function addPromoCode(promoCode) {
  return getPromoCode(promoCode).then(response => {
    // Update the source number and mode of sale, then refresh

    return API.requestPut({
      method: 'Web/Cart/%sessionKey%/Properties',
      params: {
        SourceId: response.result[0].Source.Id,
        ModeOfSaleId: response.result[0].ModeOfSaleId
      }
    }).then(() => {
      window.location.reload();
      return true;
    });
  });
}

// @TODO: Decouple this method
export function getSeatmapData(
  performanceId,
  packageNo,
  currentMOS,
  sourceCode,
  usesGeneratedSeatmap
) {
  let promises = [];

  /**
   * By default GetSeatsBriefEx requires a session key, meaning each user
   * gets a unique cache hit, so the cache only works for subsequent calls.
   *
   * If cacheableGetSeats is enabled, assuming a GetSeatsBriefExMOS method,
   * we swap out the get seats call out with one that doesn't require
   * a session key; in place we need to pass MOS and source code.
   *
   * This allows us to cache the call in a non-unique way,
   * which greatly increases the value of the cache.
   */
  if (packageNo) {
    promises = promises.concat([
      // Custom properties, removes need for session key
      API.requestGet({
        method: `TXN/Packages/${packageNo}/Seats`,
        params: {
          packageId: packageNo,
          modeOfSaleId: currentMOS,
          constituentId: 0
        }
      })
    ]);
  } else {
    promises = promises.concat([
      // Custom properties, removes need for session key
      API.requestGet({
        method: `${jsonpath}/Seats.json`,
        params: {
          performanceId: packageNo ? -1 : performanceId,
          modeOfSaleId: currentMOS,
          constituentId: 0
        }
      })
    ]);
  }

  promises = promises.concat([
    API.requestGet({
      method: `${jsonpath}/SeatStatuses.json`,
      params: {}
    })
  ]);

  if (usesGeneratedSeatmap && !packageNo) {
    promises = promises.concat([
      getScreensForPerformance(performanceId),
      getSectionsForPerformance(performanceId)
    ]);
  }

  return Promise.all(promises)
    .then(function(responses) {
      let seatsBriefResults = get(responses[0], 'result');
      let seatStatusResults = get(responses[1], 'result');
      let screensResults = get(responses[2], 'result');
      let seatsWithZones = get(responses[3], 'result');

      let seatsMap = new SeatMap();
      let statusMap = new SeatStatusMap();
      let screenMap = new ScreenMap();
      let sectionMap = new SectionMap();

      let seatsResults = enhanceSeatData(
        seatsMap.map(seatsBriefResults, true),
        statusMap.map(seatStatusResults),
        sectionMap.map(seatsWithZones),
        screensResults === undefined
          ? screenMap.map()
          : screenMap.map(screensResults.Screens)
      );

      let screenZoneData =
        seatsBriefResults && setScreenZoneData(seatsMap.map(seatsBriefResults));

      return {
        screensResults: {
          Screens:
            screensResults === undefined
              ? screenMap.map()
              : screenMap.map(screensResults.Screens)
        },
        seatsStatusResults: seatStatusResults,
        seatsResults: seatsResults,
        screenZoneData: screenZoneData
      };
    })
    .catch(fallbacks.redirectWithError);
}

// eslint-disable-next-line no-unused-vars
export function getScreensForPerformance(performanceId) {
  return API.requestGet({
    method: `${jsonpath}/ScreensForPerformance.json`
  }).then(() => {
    return API.requestGet({
      method: `${jsonpath}/PerformanceScreens.json`
    });
  });
}

// eslint-disable-next-line no-unused-vars
export function getSectionsForPerformance(performanceId) {
  return API.requestGet({
    method: `${jsonpath}/ScreensForPerformance.json`
  }).then(() => {
    return API.requestGet({
      method: `${jsonpath}/ReferenceData_Sections.json`
    });
  });
}

export function getMaxTicketAmount() {
  config = new Config().getConfig();
  if (config.customMaxTicketsLimit) {
    return Promise.resolve(config.customMaxTicketsLimit);
  } else {
    return Promise.resolve(10);
  }
}

export function getDetailsWithDiscounting(
  performanceId,
  packageNo,
  modeOfSale,
  sourceCode
) {
  if (packageNo) {
    let promises = [
      API.requestGet({
        method: `TXN/Packages/${packageNo}`
      }),
      API.requestGet({
        method: `TXN/Packages/${packageNo}/Prices`,
        params: {
          modeOfSaleId: modeOfSale,
          priceTypeId: '',
          expandPerformancePriceType: '',
          includeOnlyBasePrice: '',
          sourceId: sourceCode
        }
      }),
      API.requestGet({
        method: 'TXN/PriceTypes/Details',
        params: {
          packageId: packageNo,
          modeOfSaleId: modeOfSale,
          sourceId: sourceCode
        }
      }),
      API.requestGet({
        method: 'TXN/Performances/ZoneAvailabilities',
        params: {
          performanceIds: performanceId
        }
      }),
      API.requestGet({
        method: 'TXN/WebContents',
        params: {
          packageIds: packageNo
        }
      })
    ];

    return Promise.all(promises)
      .then(responses => {
        let pkgMap = new PackageMap();
        let mapped = pkgMap.map(responses);
        return mapped;
      })
      .catch(fallbacks.redirectWithError);
  } else {
    let promises = [
      API.requestGet({
        method: `${jsonpath}/Performances.json`
      }),
      API.requestGet({
        method: `${jsonpath}/Prices.json`,
        params: {
          performanceIds: performanceId,
          modeOfSaleId: modeOfSale,
          priceTypeId: '',
          expandPerformancePriceType: '',
          includeOnlyBasePrice: '',
          sourceId: sourceCode
        }
      }),
      API.requestGet({
        method: `${jsonpath}/PriceTypes_Details.json`,
        params: {
          performanceIds: performanceId,
          modeOfSaleId: modeOfSale,
          sourceId: sourceCode
        }
      }),
      API.requestGet({
        method: `${jsonpath}/Zone_Availabilities.json`,
        params: {
          performanceIds: performanceId
        }
      }),
      API.requestGet({
        method: `${jsonpath}/WebContents.json`,
        params: {
          productionElementIds: performanceId
        }
      })
    ];

    return Promise.all(promises).then(responses => {
      let map = new PerformanceMap();
      return map.map(responses);
    });
  }
}

export function getProductionDetail(performanceId) {
  return API.requestGet({
    method: `${jsonpath}/ProductionSummary.json`,
    params: {
      performanceIds: performanceId
    }
  }).then(response => {
    let perfMap = new PerformanceMap();
    let result = response.result.map(item => {
      return perfMap.mapPerformance([{ result: item }]);
    });

    return {
      result: result
    };
  });
}

export function getCart() {
  return API.requestGet({
    method: 'Web/Cart/%sessionKey%',
    params: {}
  }).then(function(response) {
    if (response) {
      let result = response.result;
      let map = new CartMap();
      return !isEmpty(result) ? map.map(result) : false;
    } else {
      return false;
    }
  });
}

export function ReserveTicketsSpecifiedSeats(
  performanceId,
  lineItems,
  fixedPackage
) {
  config = new Config().getConfig();
  let wheelChairSeats = filter(lineItems, item => {
    return (
      indexOf(
        config.specialRequests.excludeSeatType,
        parseInt(item.seatData.seat_type)
      ) > -1 ||
      indexOf(
        config.specialRequests.excludeHoldCode,
        parseInt(item.seatData.hc_no)
      ) > -1
    );
  });

  let leaveSingleSeats = config.specialRequests.LeaveSingleSeats
    ? config.specialRequests.LeaveSingleSeats
    : 'Y';

  if (wheelChairSeats.length) {
    leaveSingleSeats = 'Y';
  }

  if (fixedPackage) {
    return API.request({
      method: 'Web/Cart/%sessionKey%/Packages/Fixed',
      params: {
        // TODO: map these before passing through
        PriceType: lineItems.map(item => item.priceData.price_type).join(','),
        PackageId: fixedPackage,
        NumberOfSeats: lineItems.length,
        SpecialRequests: `LeaveSingleSeats=${leaveSingleSeats}`,
        RequestedSeats: lineItems.map(item => item.seatData.seat_no).join(','),
        ZoneId: 0
      }
    }).then(response => {
      return {
        result: response.result.SeatsReserved
      };
    });
  } else {
    return API.request({
      method: 'Web/Cart/%sessionKey%/Tickets',
      params: {
        // TODO: map these before passing through
        PriceType: lineItems.map(item => item.priceData.price_type).join(','),
        PerformanceId: performanceId,
        NumberOfSeats: lineItems.length,
        SpecialRequests: `LeaveSingleSeats=${leaveSingleSeats}`,
        RequestedSeats: lineItems.map(item => item.seatData.seat_no).join(','),
        ZoneId: 0
      }
    }).then(response => {
      return {
        result: response.result.SeatsReserved
      };
    });
  }
}

export function ReleaseTickets(
  iPerformanceNumber,
  iLineItemNumber,
  currentFixedPackage
) {
  if (currentFixedPackage) {
    return API.requestDelete({
      method: `Web/Cart/%sessionKey%/Packages/Fixed/${currentFixedPackage}/${iLineItemNumber}`,
      params: {}
    });
  } else {
    return API.requestDelete({
      method: `Web/Cart/%sessionKey%/Tickets/${iPerformanceNumber}/${iLineItemNumber}`,
      params: {}
    });
  }
}

export function AddPackageItemSeated(currentFixedPackage, lineItems) {
  var priceTypes = lineItems.map(item => item.priceData.price_type).join(',');
  var requestedSeats = lineItems.map(item => item.seatData.seat_no).join(',');

  return API.request({
    method: 'Web/Cart/%sessionKey%/Packages/Fixed',
    params: {
      // TODO: map these before passing through
      PriceType: priceTypes,
      PackageId: currentFixedPackage.pkg_no,
      NumberOfSeats: lineItems.length,
      SpecialRequests: 'LeaveSingleSeats=Y',
      RequestedSeats: requestedSeats,
      ZoneId: 0
    }
  }).then(response => {
    return {
      result: response.result.SeatsReserved
    };
  });
}

export function RemovePackageItem(pkgNo, liSeqNo) {
  return API.requestDelete({
    method: `Web/Cart/%sessionKey%/Packages/Fixed/${pkgNo}/${liSeqNo}`,
    params: {}
  });
}

/**
 * Custom Method (GetCurrentPromoCode)
 */
export function GetCurrentPromoCode() {
  // return API.requestGet({
  //   method: 'Web/Session/%sessionKey%'
  // }).then(response => {
  //   return getPromoCode('', response.result.SourceId).then(response => {
  //     if (response.result.constructor === Array) {
  //       if (response.result[0] !== undefined) {
  //         response.result = response.result[0].PromoCode;
  //       } else {
  //         response.result = false;
  //       }
  //     }
  //     return response;
  //   });
  // });
  return Promise.resolve(false);
}

/**
 * Custom Method (SeatUpsells)
 */
export function SeatUpsells() {
  return API.requestGet({
    method: 'SeatUpsells',
    params: {}
  }).then(function(response) {
    let result = get(response, 'result.GetLocalDataResults.LocalData');
    return result;
  });
}

export default {
  configure,
  getInitialSessionKey,
  setInitialSessionKey,
  getSessionKey,
  loginInfo,
  getSeatmapData,
  getMaxTicketAmount,
  getDetailsWithDiscounting,
  getProductionDetail,
  getCart,
  ReserveTicketsSpecifiedSeats,
  ReleaseTickets,
  AddPackageItemSeated,
  RemovePackageItem,
  GetCurrentPromoCode,
  SeatUpsells,
  addPromoCode,
  removePromoCode,
  StoreADAData
};



// WEBPACK FOOTER //
// ./src/javascripts/lib/tessitura/JSONAPI/API.js